// Vendored from @mayhem/appkit v0.1.1 (commit 78a3ab1, src/core/iconRegistry.js). This file is now owned
// by mayhem-marketplace and is the single source for the server and react/ (via a build alias).
// Keep it dependency-free (no imports): react/ and the mobile-mayhem sync script load it directly.
// A guard test (iconRegistry.test.js) enforces that.
/**
 * Stable icon registrations shared by Mayhem seller tools and storefront renderers.
 *
 * Extracted from `mayhem-marketplace/config/iconRegistry.js`, which remains authoritative until
 * consumer migration. Icon values are persisted in channel and page-element data, so existing
 * values must not be renamed.
 *
 * Image-backed registrations contain public URL paths only. The consuming app is responsible for
 * serving the corresponding assets; this module performs no I/O.
 *
 * @module @mayhem/appkit/core/iconRegistry
 */

/**
 * @typedef {object} IconAssets
 * @property {string} default Default public asset URL.
 * @property {string} [dashboard] Dashboard-specific public asset URL.
 * @property {string} [storefront] Storefront-specific public asset URL.
 */

/**
 * @typedef {object} IconRegistration
 * @property {string} value Stable icon identifier persisted by consumers.
 * @property {string} [label] Human-readable picker label.
 * @property {'fontawesome'|'image'|'text'} kind Rendering strategy.
 * @property {IconAssets} [assets] Contextual asset URLs for an image icon.
 * @property {string} [text] Text rendered in place of an image or Font Awesome glyph.
 * @property {boolean} [socialPicker] Whether the icon appears in the custom-social picker.
 * @property {boolean} [linktreePicker] Whether the icon appears in the linktree picker.
 */

/**
 * @typedef {object} SocialPlatform
 * @property {string} key Stable social-platform identifier persisted by consumers.
 * @property {readonly string[]} [aliases] Other accepted identifiers for the platform.
 * @property {string} label Human-readable platform label.
 * @property {string} icon Stable icon identifier.
 * @property {string} [prefix] URL prefix suggested by social editors.
 * @property {IconRegistration} [registration] Custom rendering registration.
 */

/**
 * @typedef {object} CustomSocialIconOption
 * @property {string} value Stable icon identifier.
 * @property {string} label Human-readable picker label.
 */

/**
 * @typedef {object} LinktreeIconOption
 * @property {string} value Stable icon identifier.
 * @property {string} text Human-readable picker label.
 */

/**
 * @typedef {object} ResolvedIcon
 * @property {string} className Stable icon identifier.
 * @property {'fontawesome'|'image'|'text'} kind Rendering strategy.
 * @property {string} imageUrl Contextual image URL, or an empty string for non-image icons.
 * @property {string} text Text fallback, or an empty string when unused.
 */

const rejectSetMutation = () => {
  throw new TypeError('Cannot mutate an exported icon registry set.');
};

/**
 * Freeze a Set and block its normal mutation methods.
 *
 * Object.freeze alone does not protect a Set's internal data.
 *
 * @template T
 * @param {Iterable<T>} values Set values.
 * @returns {ReadonlySet<T>} Read-only Set.
 */
const freezeSet = values => {
  const set = new Set(values);
  Object.defineProperties(set, {
    add: { value: rejectSetMutation },
    clear: { value: rejectSetMutation },
    delete: { value: rejectSetMutation },
  });
  return Object.freeze(set);
};

/**
 * @param {IconRegistration} registration Registration to freeze.
 * @returns {Readonly<IconRegistration>} Frozen registration.
 */
const freezeRegistration = registration => Object.freeze({
  ...registration,
  ...(registration.assets
    ? { assets: Object.freeze({ ...registration.assets }) }
    : {}),
});

/**
 * @param {SocialPlatform} platform Platform to freeze.
 * @returns {Readonly<SocialPlatform>} Frozen platform.
 */
const freezePlatform = platform => Object.freeze({
  ...platform,
  ...(platform.aliases
    ? { aliases: Object.freeze([...platform.aliases]) }
    : {}),
  ...(platform.registration
    ? { registration: freezeRegistration(platform.registration) }
    : {}),
});

/**
 * @param {string} value Stable icon identifier.
 * @param {IconAssets} assets Contextual public asset URLs.
 * @returns {IconRegistration} Image registration.
 */
const imageIcon = (value, assets) => ({ value, kind: 'image', assets });

/**
 * Supported social platforms with the marketplace's Font Awesome and custom-image vocabulary.
 *
 * @type {readonly Readonly<SocialPlatform>[]}
 */
export const SOCIAL_PLATFORMS = Object.freeze([
  { key: 'instagram', label: 'Instagram', icon: 'fa-brands fa-instagram' },
  { key: 'tiktok', label: 'TikTok', icon: 'fa-brands fa-tiktok' },
  { key: 'youtube', label: 'YouTube', icon: 'fa-brands fa-youtube' },
  {
    key: 'twitter',
    aliases: ['x'],
    label: 'Twitter/X',
    icon: 'fa-brands fa-x-twitter',
  },
  { key: 'facebook', label: 'Facebook', icon: 'fa-brands fa-facebook' },
  {
    key: 'threads',
    label: 'Threads',
    icon: 'fa-brands fa-threads',
    prefix: 'https://threads.net/',
  },
  {
    key: 'bluesky',
    label: 'BlueSky',
    icon: 'fa-brands fa-bluesky',
    prefix: 'https://bsky.app/profile/',
  },
  { key: 'shopify', label: 'Shopify', icon: 'fa-brands fa-shopify' },
  { key: 'twitch', label: 'Twitch', icon: 'fa-brands fa-twitch' },
  { key: 'pinterest', label: 'Pinterest', icon: 'fa-brands fa-pinterest' },
  { key: 'discord', label: 'Discord', icon: 'fa-brands fa-discord' },
  { key: 'snapchat', label: 'Snapchat', icon: 'fa-brands fa-snapchat' },
  { key: 'vimeo', label: 'Vimeo', icon: 'fa-brands fa-vimeo' },
  {
    key: 'etsy',
    label: 'Etsy',
    icon: 'fa-custom fa-etsy',
    registration: imageIcon('fa-custom fa-etsy', {
      default: '/images/icons/etsy-black.png',
      dashboard: '/images/icons/etsy-black.png',
      storefront: '/images/icons/etsy-orange.png',
    }),
  },
  {
    key: 'clapper',
    label: 'Clapper',
    icon: 'fa-custom fa-clapper',
    registration: imageIcon('fa-custom fa-clapper', {
      default: '/images/icons/clapper-black.png',
      dashboard: '/images/icons/clapper-black.png',
      storefront: '/images/icons/clapper-orange.png',
    }),
  },
  {
    key: 'substack',
    label: 'Substack',
    icon: 'fa-custom fa-substack',
    registration: imageIcon('fa-custom fa-substack', {
      default: '/images/icons/substack.png',
    }),
  },
  {
    key: 'patreon',
    label: 'Patreon',
    icon: 'fa-custom fa-patreon',
    registration: imageIcon('fa-custom fa-patreon', {
      default: '/images/icons/patreon-black.png',
    }),
  },
  {
    key: 'kofi',
    label: 'Ko-Fi',
    icon: 'fa-custom fa-kofi',
    registration: imageIcon('fa-custom fa-kofi', {
      default: '/images/icons/kofi_symbol.png',
    }),
  },
  { key: 'mastodon', label: 'Mastodon', icon: 'fa-brands fa-mastodon' },
  {
    key: 'pixelfed',
    label: 'Pixelfed',
    icon: 'fa-custom fa-pixelfed',
    registration: { value: 'fa-custom fa-pixelfed', kind: 'text', text: 'P' },
  },
  { key: 'spark', label: 'Spark', icon: 'fa-solid fa-star' },
  { key: 'skylight', label: 'Skylight', icon: 'fa-solid fa-sun' },
  { key: 'lemon8', label: 'Lemon8', icon: 'fa-solid fa-lemon' },
  { key: 'rednote', label: 'Rednote', icon: 'fa-solid fa-music' },
  { key: 'fanbase', label: 'Fanbase', icon: 'fa-solid fa-user' },
  { key: 'poshmark', label: 'Poshmark', icon: 'fa-solid fa-infinity' },
  { key: 'mercari', label: 'Mercari', icon: 'fa-solid fa-m' },
  { key: 'vero', label: 'Vero', icon: 'fa-solid fa-v' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'fa-brands fa-linkedin' },
  { key: 'ghost', label: 'Ghost', icon: 'fa-solid fa-ghost' },
  { key: 'upscrolled', label: 'UpScrolled', icon: 'fa-solid fa-square-caret-up' },
].map(freezePlatform));

const CUSTOM_SOCIAL_FONT_AWESOME_OPTIONS = [
  ['fa-solid fa-link', 'Generic Link'],
  ['fa-solid fa-globe', 'Website'],
  ['fa-solid fa-envelope', 'Email'],
  ['fa-solid fa-phone', 'Phone'],
  ['fa-solid fa-store', 'Store'],
  ['fa-solid fa-camera', 'Camera'],
  ['fa-solid fa-music', 'Music'],
  ['fa-solid fa-video', 'Video'],
  ['fa-solid fa-microphone', 'Podcast'],
  ['fa-solid fa-book', 'Blog'],
  ['fa-solid fa-heart', 'Favorites'],
  ['fa-solid fa-star', 'Reviews'],
  ['fa-solid fa-users', 'Community'],
  ['fa-solid fa-comments', 'Forum'],
  ['fa-solid fa-calendar', 'Events'],
  ['fa-solid fa-map-marker-alt', 'Location'],
  ['fa-solid fa-briefcase', 'Career'],
  ['fa-solid fa-graduation-cap', 'Education'],
  ['fa-solid fa-heartbeat', 'Health'],
  ['fa-solid fa-futbol', 'Sports'],
  ['fa-solid fa-palette', 'Art'],
  ['fa-solid fa-code', 'Programming'],
  ['fa-solid fa-cog', 'Settings'],
  ['fa-solid fa-shield-alt', 'Security'],
  ['fa-solid fa-lock', 'Privacy'],
  ['fa-solid fa-bell', 'Notifications'],
  ['fa-solid fa-chart-line', 'Analytics'],
  ['fa-solid fa-file-alt', 'Documents'],
  ['fa-solid fa-certificate', 'Certifications'],
  ['fa-solid fa-trophy', 'Achievements'],
  ['fa-solid fa-lightbulb', 'Ideas'],
  ['fa-solid fa-rocket', 'Launch'],
  ['fa-solid fa-wrench', 'Tools'],
  ['fa-solid fa-sitemap', 'Network'],
  ['fa-solid fa-users-cog', 'Team'],
  ['fa-solid fa-handshake', 'Partnerships'],
];

const LINKTREE_FONT_AWESOME_OPTIONS = [
  ['', '- Select an Icon -'],
  ['fa-brands fa-facebook', 'Facebook'],
  ['fa-brands fa-bluesky', 'BlueSky'],
  ['fa-brands fa-twitter', 'Twitter'],
  ['fa-brands fa-instagram', 'Instagram'],
  ['fa-brands fa-threads', 'Threads'],
  ['fa-brands fa-tiktok', 'TikTok'],
  ['fa-brands fa-linkedin', 'LinkedIn'],
  ['fa-brands fa-github', 'GitHub'],
  ['fa-brands fa-discord', 'Discord'],
  ['fa-brands fa-youtube', 'YouTube'],
  ['fa-brands fa-wordpress', 'WordPress'],
  ['fa-brands fa-slack', 'Slack'],
  ['fa-brands fa-paypal', 'PayPal'],
  ['fa-brands fa-kickstarter', 'Kickstarter'],
  ['fa-brands fa-vimeo', 'Vimeo'],
  ['fa-brands fa-airbnb', 'AirBnb'],
  ['fa-brands fa-whatsapp', 'Whatsapp'],
  ['fa-brands fa-telegram', 'Telegram'],
  ['fa-brands fa-behance', 'Behance'],
  ['fa-brands fa-product-hunt', 'Product Hunt'],
  ['fa-brands fa-pinterest', 'Pinterest'],
  ['fa-brands fa-soundcloud', 'Soundcloud'],
  ['fa-brands fa-spotify', 'Spotify'],
  ['fa-brands fa-amazon', 'Amazon'],
  ['fa-brands fa-twitch', 'Twitch'],
  ['fa-brands fa-shopify', 'Shopify'],
  ['fa-brands fa-medium', 'Medium'],
  ['fa-brands fa-cuttlefish', 'Cuttlefish/Cara'],
  ['fa-solid fa-rss', 'Website/Feed'],
  ['fa-solid fa-blog', 'Blog'],
  ['fa-solid fa-pepper-hot', 'Hot Pepper'],
  ['fa-solid fa-lock', 'Lock'],
  ['fa-solid fa-calendar', 'Calendar'],
  ['fa-solid fa-bell', 'Bell'],
  ['fa-solid fa-birthday-cake', 'Birthday Cake'],
  ['fa-solid fa-book', 'Book'],
  ['fa-solid fa-camera', 'Camera'],
  ['fa-solid fa-certificate', 'Certificate'],
  ['fa-solid fa-chart-line', 'Chart Line'],
  ['fa-solid fa-check', 'Check'],
  ['fa-solid fa-cloud', 'Cloud'],
  ['fa-solid fa-coffee', 'Coffee'],
  ['fa-solid fa-comment', 'Comment'],
  ['fa-solid fa-compass', 'Compass'],
  ['fa-solid fa-desktop', 'Desktop'],
  ['fa-solid fa-envelope', 'Envelope'],
  ['fa-solid fa-eye', 'Eye'],
  ['fa-solid fa-f', 'F - Letter'],
  ['fa-solid fa-film', 'Film'],
  ['fa-solid fa-file', 'File'],
  ['fa-solid fa-flag', 'Flag'],
  ['fa-solid fa-flask', 'Flask'],
  ['fa-solid fa-globe', 'Globe'],
  ['fa-solid fa-heart', 'Heart'],
  ['fa-solid fa-key', 'Key'],
  ['fa-solid fa-lightbulb', 'Lightbulb'],
  ['fa-solid fa-lemon', 'Lemon'],
  ['fa-solid fa-link', 'Link'],
  ['fa-solid fa-map', 'Map'],
  ['fa-solid fa-microphone', 'Microphone'],
  ['fa-solid fa-mobile', 'Mobile'],
  ['fa-solid fa-music', 'Music'],
  ['fa-solid fa-paint-brush', 'Paint Brush'],
  ['fa-solid fa-paper-plane', 'Paper Plane'],
  ['fa-solid fa-pen', 'Pen'],
  ['fa-solid fa-phone', 'Phone'],
  ['fa-solid fa-photo-video', 'Photo Video'],
  ['fa-solid fa-podcast', 'Podcast'],
  ['fa-solid fa-print', 'Print'],
  ['fa-solid fa-question', 'Question'],
  ['fa-solid fa-search', 'Search'],
  ['fa-solid fa-shopping-cart', 'Shopping Cart'],
  ['fa-solid fa-star', 'Star'],
  ['fa-solid fa-tag', 'Tag'],
  ['fa-solid fa-thumbs-up', 'Thumbs Up'],
  ['fa-solid fa-trophy', 'Trophy'],
  ['fa-solid fa-tv', 'TV'],
  ['fa-solid fa-user', 'User'],
  ['fa-solid fa-video', 'Video'],
  ['fa-solid fa-wifi', 'Wifi'],
  ['fa-solid fa-wrench', 'Wrench'],
  ['fa-solid fa-yen-sign', 'Yen Sign'],
  ['fa-solid fa-graduation-cap', 'Graduation Cap'],
  ['fa-solid fa-umbrella', 'Umbrella'],
  ['fa-solid fa-rocket', 'Rocket'],
  ['fa-solid fa-leaf', 'Leaf'],
  ['fa-solid fa-ice-cream', 'Ice Cream'],
  ['fa-solid fa-hat-wizard', 'Hat Wizard'],
  ['fa-solid fa-guitar', 'Guitar'],
  ['fa-solid fa-gem', 'Gem'],
  ['fa-solid fa-futbol', 'Futbol'],
  ['fa-solid fa-fire', 'Fire'],
  ['fa-solid fa-fighter-jet', 'Fighter Jet'],
  ['fa-solid fa-fan', 'Fan'],
  ['fa-solid fa-drum', 'Drum'],
  ['fa-solid fa-dove', 'Dove'],
  ['fa-solid fa-dice', 'Dice'],
  ['fa-solid fa-crown', 'Crown'],
  ['fa-solid fa-cocktail', 'Cocktail'],
  ['fa-solid fa-chess', 'Chess'],
  ['fa-solid fa-bug', 'Bug'],
  ['fa-solid fa-anchor', 'Anchor'],
  ['fa-solid fa-ambulance', 'Ambulance'],
  ['fa-solid fa-air-freshener', 'Air Freshener'],
  [
    'fa-solid fa-american-sign-language-interpreting',
    'American Sign Language Interpreting',
  ],
  ['fa-solid fa-archway', 'Archway'],
  ['fa-solid fa-award', 'Award'],
  ['fa-solid fa-balance-scale', 'Balance Scale'],
  ['fa-solid fa-baby', 'Baby'],
  ['fa-solid fa-baby-carriage', 'Baby Carriage'],
  ['fa-solid fa-balance-scale-left', 'Balance Scale Left'],
  ['fa-solid fa-balance-scale-right', 'Balance Scale Right'],
  ['fa-solid fa-bath', 'Bath'],
  ['fa-solid fa-bed', 'Bed'],
  ['fa-solid fa-biking', 'Biking'],
  ['fa-solid fa-binoculars', 'Binoculars'],
  ['fa-solid fa-stroopwafel', 'Yarn'],
  ['fa-solid fa-pizza-slice', 'Pizza'],
];

const customRegistrations = SOCIAL_PLATFORMS
  .filter(platform => platform.registration)
  .map(platform => ({
    label: platform.label,
    socialPicker: true,
    linktreePicker: true,
    ...platform.registration,
  }));

/**
 * @param {[string, string]} option Icon value and label.
 * @param {Partial<IconRegistration>} [uses] Picker flags.
 * @returns {IconRegistration} Font Awesome registration.
 */
const fontAwesomeRegistration = ([value, label], uses = {}) => ({
  value,
  label,
  kind: 'fontawesome',
  ...uses,
});

const registrationsByValue = new Map();

for (const registration of LINKTREE_FONT_AWESOME_OPTIONS.map(option => (
  fontAwesomeRegistration(option, { linktreePicker: true })
))) {
  registrationsByValue.set(registration.value, registration);
}

for (const registration of CUSTOM_SOCIAL_FONT_AWESOME_OPTIONS.map(option => (
  fontAwesomeRegistration(option, { socialPicker: true })
))) {
  const existing = registrationsByValue.get(registration.value);
  registrationsByValue.set(registration.value, { ...existing, ...registration });
}

for (const platform of SOCIAL_PLATFORMS) {
  if (!registrationsByValue.has(platform.icon)) {
    registrationsByValue.set(platform.icon, {
      value: platform.icon,
      label: platform.label,
      kind: 'fontawesome',
    });
  }
}

for (const registration of customRegistrations) {
  registrationsByValue.set(registration.value, registration);
}

/**
 * Complete icon registry, including Font Awesome, image-backed, and text-backed entries.
 *
 * @type {readonly Readonly<IconRegistration>[]}
 */
export const ICON_REGISTRY = Object.freeze(
  Array.from(registrationsByValue.values(), freezeRegistration),
);

/**
 * Every non-empty registered icon value.
 *
 * @type {ReadonlySet<string>}
 */
export const ICON_VALUES = freezeSet(
  ICON_REGISTRY.map(icon => icon.value).filter(Boolean),
);

/**
 * Every supported social-platform key.
 *
 * @type {ReadonlySet<string>}
 */
export const SOCIAL_PLATFORM_KEYS = freezeSet(
  SOCIAL_PLATFORMS.map(platform => platform.key),
);

/**
 * Icon values accepted for social links, including the persisted legacy Twitter value.
 *
 * @type {ReadonlySet<string>}
 */
export const SOCIAL_ICON_VALUES = freezeSet([
  ...SOCIAL_PLATFORMS.map(platform => platform.icon),
  ...ICON_REGISTRY.filter(icon => icon.socialPicker).map(icon => icon.value),
  'fa-brands fa-twitter',
]);

/**
 * Options shown in the custom-social icon picker.
 *
 * @type {readonly Readonly<CustomSocialIconOption>[]}
 */
export const CUSTOM_SOCIAL_ICON_OPTIONS = Object.freeze(
  ICON_REGISTRY
    .filter(icon => icon.socialPicker)
    .map(icon => Object.freeze({ value: icon.value, label: icon.label })),
);

/**
 * Options shown in the linktree icon picker.
 *
 * @type {readonly Readonly<LinktreeIconOption>[]}
 */
export const LINKTREE_ICON_OPTIONS = Object.freeze(
  ICON_REGISTRY
    .filter(icon => icon.linktreePicker)
    .map(icon => Object.freeze({ value: icon.value, text: icon.label })),
);

/**
 * Resolve a registered icon value for a rendering context.
 *
 * @param {*} value Candidate icon value.
 * @param {string} [context='default'] Asset context such as `dashboard` or `storefront`.
 * @param {string} [fallback=''] Registered fallback value.
 * @returns {ResolvedIcon|null} Rendering metadata, or `null` for an unregistered value.
 */
export function resolveIcon(value, context = 'default', fallback = '') {
  const normalized = typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
  const registration = (normalized ? registrationsByValue.get(normalized) : null)
    || (fallback && registrationsByValue.get(fallback));

  if (!registration) return null;

  const imageUrl = registration.kind === 'image'
    ? registration.assets?.[context] || registration.assets?.default || ''
    : '';

  return {
    className: registration.value,
    kind: registration.kind,
    imageUrl,
    text: registration.text || '',
  };
}

/**
 * Normalize an icon value to its stable registered representation.
 *
 * @param {*} value Candidate icon value.
 * @param {string} [fallback=''] Registered fallback value.
 * @returns {string} Registered icon value, or the supplied fallback when resolution fails.
 */
export function normalizeIconValue(value, fallback = '') {
  return resolveIcon(value, 'default', fallback)?.className || fallback;
}
